import React, { useState } from 'react';
import { generatePassword, copyToClipboard } from '../../utils/passwordGenerator';
import api from '../../services/api';

interface AddPortalUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: {
    id: number;
    name: string;
    email?: string;
  };
  onSuccess: () => void;
}

const AddPortalUserModal: React.FC<AddPortalUserModalProps> = ({
  isOpen,
  onClose,
  customer,
  onSuccess,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(customer.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [setPasswordManually, setSetPasswordManually] = useState(false);
  const [canPlaceOrders, setCanPlaceOrders] = useState(true);
  const [canViewInvoices, setCanViewInvoices] = useState(true);
  const [sendEmail, setSendEmail] = useState(true); // Default to true for invitation flow
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState<{email: string; password: string | null; invitationSent: boolean} | null>(null);

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setPassword(newPassword);
    setShowPassword(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // If setting password manually, require it
    if (setPasswordManually && !password) {
      setError('Please enter a password or uncheck "Set password manually"');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post(`/customers/${customer.id}/create_portal_user/`, {
        email,
        first_name: firstName,
        last_name: lastName,
        password: setPasswordManually ? password : undefined, // Only send password if manually setting
        can_place_orders: canPlaceOrders,
        can_view_invoices: canViewInvoices,
        send_welcome_email: sendEmail,
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      // Track whether this was an invitation or manual password setup
      const wasInvitation = !setPasswordManually && sendEmail;
      setCreatedCredentials({
        email,
        password: setPasswordManually ? password : null,
        invitationSent: wasInvitation
      });
      setSuccess(true);
      onSuccess();
    } catch (err: any) {
      // Handle axios error responses
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create portal user';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCredentials = async () => {
    if (!createdCredentials || !createdCredentials.password) return;

    const text = `Portal Login Credentials

URL: ${window.location.origin}/portal/login
Email: ${createdCredentials.email}
Password: ${createdCredentials.password}`;

    const copied = await copyToClipboard(text);
    if (copied) {
      alert('Credentials copied to clipboard!');
    }
  };

  const handleClose = () => {
    setFirstName('');
    setLastName('');
    setEmail(customer.email || '');
    setPassword('');
    setShowPassword(false);
    setSetPasswordManually(false);
    setCanPlaceOrders(true);
    setCanViewInvoices(true);
    setSendEmail(true);
    setError('');
    setSuccess(false);
    setCreatedCredentials(null);
    onClose();
  };

  if (!isOpen) return null;

  // Success screen - different for invitation vs manual password
  if (success && createdCredentials) {
    return (
      <div style={styles.overlay} onClick={handleClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.header}>
            <h2 style={styles.title}>Portal User Created!</h2>
            <button onClick={handleClose} style={styles.closeButton}>×</button>
          </div>

          <div style={styles.body}>
            {createdCredentials.invitationSent ? (
              // Invitation flow success
              <>
                <div style={styles.successIconContainer}>
                  <span style={styles.successIcon}>✉️</span>
                </div>
                <p style={styles.successMessageCenter}>
                  Invitation sent to <strong>{createdCredentials.email}</strong>
                </p>
                <p style={styles.successDescription}>
                  They will receive an email with a link to set their password and access the portal.
                  The link expires in 48 hours.
                </p>
              </>
            ) : createdCredentials.password ? (
              // Manual password flow success
              <>
                <p style={styles.successMessage}>
                  Login credentials for <strong>{createdCredentials.email}</strong>:
                </p>

                <div style={styles.credentialsBox}>
                  <div style={styles.credentialRow}>
                    <strong>Portal URL:</strong> {window.location.origin}/portal/login
                  </div>
                  <div style={styles.credentialRow}>
                    <strong>Email:</strong> {createdCredentials.email}
                  </div>
                  <div style={styles.credentialRow}>
                    <strong>Password:</strong> {createdCredentials.password}
                  </div>
                </div>

                <div style={styles.actions}>
                  <button onClick={handleCopyCredentials} style={styles.copyButton}>
                    Copy Credentials
                  </button>
                  <button onClick={handleClose} style={styles.doneButton}>
                    Done
                  </button>
                </div>
              </>
            ) : (
              // No email, no password - user needs to be notified manually
              <>
                <p style={styles.successMessage}>
                  Portal user created for <strong>{createdCredentials.email}</strong>
                </p>
                <p style={styles.warningText}>
                  No welcome email was sent and no password was set.
                  The user will need to be contacted separately to set up their password.
                </p>
              </>
            )}

            {(createdCredentials.invitationSent || !createdCredentials.password) && (
              <div style={styles.actions}>
                <button onClick={handleClose} style={styles.doneButton}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Add Portal User</h2>
          <button onClick={handleClose} style={styles.closeButton}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={styles.body}>
          <div style={styles.customerInfo}>
            Customer: <strong>{customer.name}</strong>
          </div>

          {error && (
            <div style={styles.errorBox}>
              {error}
            </div>
          )}

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label htmlFor="firstName" style={styles.label}>
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={styles.input}
                placeholder="First name"
              />
            </div>
            <div style={styles.formGroup}>
              <label htmlFor="lastName" style={styles.label}>
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={styles.input}
                placeholder="Last name"
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="email" style={styles.label}>
              Email <span style={styles.required}>*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={styles.input}
              placeholder="email@example.com"
            />
          </div>

          <div style={styles.separator}></div>

          <div style={styles.permissionsSection}>
            <div style={styles.sectionLabel}>PERMISSIONS</div>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={canPlaceOrders}
                onChange={(e) => setCanPlaceOrders(e.target.checked)}
              />
              <span>Can place orders</span>
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={canViewInvoices}
                onChange={(e) => setCanViewInvoices(e.target.checked)}
              />
              <span>Can view invoices</span>
            </label>
          </div>

          <div style={styles.separator}></div>

          {/* Welcome Email Option */}
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <span>Send welcome email with password setup link</span>
          </label>
          {sendEmail && !setPasswordManually && (
            <p style={styles.helpText}>
              The user will receive an email with a link to create their own password.
            </p>
          )}

          <div style={styles.separator}></div>

          {/* Collapsible Manual Password Section */}
          <div
            style={styles.collapsibleHeader}
            onClick={() => setSetPasswordManually(!setPasswordManually)}
          >
            <span style={styles.collapsibleIcon}>{setPasswordManually ? '▼' : '▶'}</span>
            <span style={styles.collapsibleTitle}>Set password manually (optional)</span>
          </div>

          {setPasswordManually && (
            <div style={styles.passwordSection}>
              <div style={styles.formGroup}>
                <label htmlFor="password" style={styles.label}>
                  Password
                </label>
                <div style={styles.passwordGroup}>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{...styles.input, flex: 1}}
                    placeholder="Enter password"
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.iconButton}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    style={styles.generateButton}
                  >
                    Generate
                  </button>
                </div>
                {password && (
                  <div style={styles.generatedPassword}>
                    Password: <strong>{showPassword ? password : '••••••••'}</strong>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(password)}
                      style={styles.copySmallButton}
                    >
                      Copy
                    </button>
                  </div>
                )}
                <p style={styles.helpTextSmall}>
                  Minimum 8 characters. You'll need to share this password with the user.
                </p>
              </div>
            </div>
          )}

          <div style={styles.separator}></div>

          <div style={styles.actions}>
            <button
              type="button"
              onClick={handleClose}
              style={styles.cancelButton}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.submitButton,
                ...(loading ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? 'Creating...' : (sendEmail && !setPasswordManually ? 'Send Invitation' : 'Create Portal User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: '#6b7280',
    lineHeight: 1,
  },
  body: {
    padding: '1.5rem',
  },
  customerInfo: {
    padding: '0.75rem',
    backgroundColor: '#f3f4f6',
    borderRadius: '4px',
    marginBottom: '1.5rem',
    fontSize: '0.95rem',
  },
  formRow: {
    display: 'flex',
    gap: '1rem',
  },
  formGroup: {
    marginBottom: '1.25rem',
    flex: 1,
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '0.5rem',
  },
  required: {
    color: '#dc2626',
  },
  input: {
    width: '100%',
    padding: '0.625rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
  },
  passwordGroup: {
    display: 'flex',
    gap: '0.5rem',
  },
  iconButton: {
    padding: '0.625rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: 'white',
    fontSize: '1rem',
  },
  generateButton: {
    padding: '0.625rem 1rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  generatedPassword: {
    marginTop: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#f0fdf4',
    borderRadius: '4px',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  copySmallButton: {
    padding: '0.25rem 0.5rem',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '0.75rem',
  },
  separator: {
    height: '1px',
    backgroundColor: '#e5e7eb',
    margin: '1.5rem 0',
  },
  permissionsSection: {
    marginBottom: '1rem',
  },
  sectionLabel: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#6b7280',
    marginBottom: '0.75rem',
    letterSpacing: '0.05em',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.95rem',
    cursor: 'pointer',
    marginBottom: '0.5rem',
  },
  helpText: {
    fontSize: '0.8rem',
    color: '#6b7280',
    marginTop: '0.5rem',
    marginLeft: '1.5rem',
    fontStyle: 'italic',
  },
  helpTextSmall: {
    fontSize: '0.75rem',
    color: '#6b7280',
    marginTop: '0.5rem',
  },
  collapsibleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    padding: '0.5rem 0',
    userSelect: 'none',
  },
  collapsibleIcon: {
    fontSize: '0.75rem',
    color: '#6b7280',
    width: '1rem',
  },
  collapsibleTitle: {
    fontSize: '0.95rem',
    color: '#374151',
  },
  passwordSection: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },
  actions: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
  submitButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#1e3a8a',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.875rem',
  },
  successIconContainer: {
    textAlign: 'center',
    marginBottom: '1rem',
  },
  successIcon: {
    fontSize: '3rem',
  },
  successMessage: {
    fontSize: '1rem',
    color: '#374151',
    marginBottom: '1.5rem',
  },
  successMessageCenter: {
    fontSize: '1.1rem',
    color: '#374151',
    textAlign: 'center',
    marginBottom: '0.5rem',
  },
  successDescription: {
    fontSize: '0.9rem',
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: '1.5rem',
  },
  warningText: {
    fontSize: '0.9rem',
    color: '#92400e',
    backgroundColor: '#fef3c7',
    padding: '0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  credentialsBox: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #86efac',
    padding: '1rem',
    borderRadius: '4px',
    marginBottom: '1.5rem',
  },
  credentialRow: {
    marginBottom: '0.5rem',
    fontSize: '0.95rem',
    wordBreak: 'break-all',
  },
  copyButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
  doneButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#1e3a8a',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
  },
};

export default AddPortalUserModal;
